/**
 * ------------------------------------------- Configuration of Flows -------------------------------------------
 *
 *       q - r - s  <- optional flow
 *       |
 *   a - b - c - d - e - f  <- main flow
 *       | /         |
 *       g - h       i - j  <- optional flow
 *                       |
 *                       k - l - m - n - o  <- optional flow
 *
 * mainFlow = [a, b, c, d, e, f]
 * Optional flows: qFlow = [q, r, s]; gFlow = [g, h]; iFlow = [i, j]; kFlow = [k, l, m, n, o]
 * Each flow is described by an array of page paths (strings) starting at index 0 and ending at the last index.
 * !!!Note that all page paths must be unique!!!!, otherwise a secondary identifier must be used for discrimination.
 *
 * optionalFlowMap = {b: {qOption: qFlow, gOption: gFlow}, c: {gOption: gFlow}, e: {iOption: iFlow}, j: {kOption: kFlow}}
 * The optional flow map describes the children of each page in terms of secondary (optional) flows.
 *
 * qOption = RouteOption.qFlowIdentifier, gOption = RouteOption.gFlowIdentifier, etc...
 * Each page (parent) may have several optional routes (children). A particular child of a page is specified
 * by referencing a RouteOption parameter (enum). See f.e. the optional routes accessible from page b.
 *
 * Once the end of an optional flow is reached, !!!the next page will be the parent of that optional flow!!!
 */

import { originationPagePaths } from '@/routes/originationRoutes'
import { marketingPagePaths } from '@/routes/marketingRoutes'
import { checkAreAllPathsUnique, checkPathsMatch, RouteOption, tryGetNextFlowPath, tryGetOptionalPath } from '@/flow/flowUtility'
import { authPagePaths } from '@/routes/authRoutes'
import { sharedPagePaths } from '@/routes/sharedRoutes'

export const originationEntryPagePaths: string[] = [marketingPagePaths.LANDING, marketingPagePaths.LANDING_JOIN]

export const originationReturnPagePathPairs: string[][] = [
    [authPagePaths.OFFER_RETURN, originationPagePaths.OFFER_PREVIEW],
    [authPagePaths.INCOME_VERIFICATION_RETURN, originationPagePaths.EMPLOYMENT_INCOME_VERIFICATION],
]

// TODO: can we grey out the web browser back button on Onboarding as a flag?
export const originationBackGuardPagePaths: string[] = [
    originationPagePaths.OFFER_PREVIEW,
    originationPagePaths.IDENTITY_QUESTIONS,
    originationPagePaths.CO_APPLICANT_IDENTITY_QUESTIONS,
    originationPagePaths.AWAITING_MANUAL_INCOME_VERIFICATION,
    originationPagePaths.SIGN_DOCUMENTS,
    originationPagePaths.OFFER_ADD_CO_APPLICANT_ON_FAILURE,
    originationPagePaths.STATED_INCOME,
    sharedPagePaths.THANKS,
    sharedPagePaths.LOAN_NOT_APPROVED,
    originationPagePaths.WELCOME,
]

export const originationFlow: string[] = [
    originationPagePaths.INSURANCE_UPLOAD,
    originationPagePaths.CONFIRM_VEHICLE,
    originationPagePaths.PAYOFF_AMOUNT,
    originationPagePaths.OFFER_PREVIEW,
    originationPagePaths.FORWARD_TO_NOTARIZATION,
]

const identityQuestionsFlow: string[] = [originationPagePaths.IDENTITY_QUESTIONS]
const ssnVerificationFlow: string[] = [originationPagePaths.SSN_VERIFICATION]
const identityFlowGroup: Map<string, string[]> = new Map([
    [RouteOption.identityQuestions, identityQuestionsFlow],
    [RouteOption.ssnVerification, ssnVerificationFlow],
])

const coApplicantSsnVerificationFlow: string[] = [originationPagePaths.CO_APPLICANT_SSN_VERIFICATION]
const coApplicantIdentityQuestionsFlow: string[] = [originationPagePaths.CO_APPLICANT_IDENTITY_QUESTIONS]
const coApplicantIdentityFlowGroup: Map<string, string[]> = new Map([
    [RouteOption.identityQuestions, coApplicantIdentityQuestionsFlow],
    [RouteOption.coApplicantSsnVerification, coApplicantSsnVerificationFlow],
])

const experianFrozenFlow: string[] = [originationPagePaths.EXPERIAN_FROZEN]
const coApplicantExperianFrozenFlow: string[] = [originationPagePaths.CO_APPLICANT_EXPERIAN_FROZEN]
const addCoApplicantOnFailureFlow: string[] = [
    originationPagePaths.OFFER_ADD_CO_APPLICANT_ON_FAILURE,
    originationPagePaths.ADD_CO_APPLICANT_INFO,
    // Manual redirection to co applicant verify identity from previous page...
]
const remedyNameMismatchFlow = [originationPagePaths.REMEDY_NAME_MISMATCH]
const preQualificationFlowGroup: Map<string, string[]> = new Map([
    [RouteOption.experianFrozen, experianFrozenFlow],
    [RouteOption.coApplicantExperianFrozen, coApplicantExperianFrozenFlow],
    [RouteOption.addCoApplicantOnFailure, addCoApplicantOnFailureFlow],
    [RouteOption.remedyNameMismatch, remedyNameMismatchFlow],
])

const workEmailFlow = [originationPagePaths.EMPLOYER_EMAIL, originationPagePaths.EMPLOYER_EMAIL_CODE_VERIFICATION]
const payStubsFlow = [originationPagePaths.PAY_STUB_VERIFICATION]
const taxReturnFlow = [originationPagePaths.TAX_RETURN_VERIFICATION]
const otherIncomeVerificationFlow = [originationPagePaths.OTHER_INCOME_VERIFICATION]
const incomeVerificationFlowGroup: Map<string, string[]> = new Map([
    [RouteOption.taxReturnVerify, taxReturnFlow],
    [RouteOption.payStubsVerify, payStubsFlow],
    [RouteOption.employerEmailVerify, workEmailFlow],
    [RouteOption.otherIncomeVerify, otherIncomeVerificationFlow],
])

const supportingInfoFlow = [originationPagePaths.PROVIDE_SUPPORTING_INFORMATION]
const awaitingManualIncomeVerificationFlow = [originationPagePaths.AWAITING_MANUAL_INCOME_VERIFICATION]
const jointIncomeOfferContingencyFlow = [originationPagePaths.AWAITING_JOINT_INCOME_OFFER_CONTINGENCY]
const offerPreviewFlowGroup: Map<string, string[]> = new Map([
    [RouteOption.supportingInfo, supportingInfoFlow],
    [RouteOption.awaitingManualIncomeVerification, awaitingManualIncomeVerificationFlow],
    [RouteOption.awaitingJointIncomeOfferContingency, jointIncomeOfferContingencyFlow],
    [RouteOption.remedyNameMismatch, remedyNameMismatchFlow],
])

const optionalFlowMap: Map<string, Map<string, string[]>> = new Map([
    [originationPagePaths.VERIFY_IDENTITY, identityFlowGroup],
    [originationPagePaths.CO_APPLICANT_VERIFY_IDENTITY, coApplicantIdentityFlowGroup],
    [originationPagePaths.PRE_QUALIFICATION, preQualificationFlowGroup],
    [originationPagePaths.EMPLOYMENT_INCOME_VERIFICATION, incomeVerificationFlowGroup],
    [originationPagePaths.CO_APPLICANT_EMPLOYMENT_INCOME_VERIFICATION, incomeVerificationFlowGroup],
    [originationPagePaths.OFFER_PREVIEW, offerPreviewFlowGroup],
])

// Could be a unit test
if (!checkAreAllPathsUnique(originationFlow, optionalFlowMap)) {
    throw new Error('origination page paths not unique!')
}

export const getOriginationNextRouteWithCurrentPath = (currentPath: string, routeOption?: RouteOption): string | null => {
    if (originationEntryPagePaths.findIndex((path) => checkPathsMatch(path, currentPath)) >= 0) {
        return originationFlow[0]
    }

    for (const [path, nextPath] of originationReturnPagePathPairs) {
        if (checkPathsMatch(path, currentPath)) {
            return nextPath
        }
    }

    let nextPath: string | null
    if (routeOption) {
        nextPath = tryGetOptionalPath(currentPath, optionalFlowMap, routeOption)
    } else {
        nextPath = tryGetNextFlowPath(currentPath, originationFlow, optionalFlowMap)
    }
    return nextPath
}
